<script lang="ts">
  import { onMount } from "svelte"
  import { Pulse } from "svelte-loading-spinners"
  import { xhr, toast, formatter, formatDate, dateConverter } from "$app/utils"
  import UpdateCreditCard from "./Billing/UpdateCreditCard.svelte"
  import ModifyStandard from "./Billing/ModifyStandard.svelte"
  import UpgradeFree from "./Billing/UpgradeFree.svelte"
  import { appStore } from "$app/store.svelte"
  import { signupStore } from "$app/signupstore.svelte"

  let showUpgradeModifyStatus:
    | SubscriptionTypesType
    | "upgradefree"
    | "modifystandard" = $state(signupStore.subscription.subscription_type)
  let stripe_data: any = $state(null)
  let stripe_products: any[] = []
  let stripe_subscription: any = $state(null)
  let stripe_invoices: any[] = $state([])
  let stripe_upcoming_invoice: any = $state(null)

  let isLoading = $state(true)
  async function initialize(): Promise<void> {
    isLoading = true
    try {
      const resp = await xhr.get(`/plans/${appStore.plan.id}/admin/billing`)

      let data = resp.data

      stripe_products = data.stripe_products
      stripe_data = data.stripe_data
      signupStore.stripe_data = data.stripe_data
      signupStore.subscription = data.subscription

      stripe_subscription = stripe_data.subscription
      if (data.stripe_invoices?.data) {
        stripe_invoices = data.stripe_invoices.data
      }
      stripe_upcoming_invoice = data.stripe_upcoming_invoice
    } catch (e) {
      console.log(e)
      toast("There was an error loading the billing data", "error")
    } finally {
      isLoading = false
    }
  }

  onMount(async () => {
    initialize()
  })
</script>

<div class="flex flex-col">
  <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
    <div
      class="py-2 text-lg align-middle inline-block min-w-full sm:px-6 lg:px-8"
    >
      <div class="flex justify-between items-start">
        <h2 class="text-2xl font-semibold pb-1 mb-3">Billing</h2>
        <div>
          {#if showUpgradeModifyStatus === "upgradefree"}
            <button
              class=" !text-blue-500 px-4 border border-blue-100 py-2 rounded hover:bg-blue-50"
              onclick={() => (showUpgradeModifyStatus = "free")}
            >
              Cancel Upgrade
            </button>
          {/if}
        </div>
      </div>
      {#if isLoading}
        <div class="py-12 flex justify-center w-full">
          <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
        </div>
      {:else if showUpgradeModifyStatus === "upgradefree"}
        <div class="mt-4">
          <UpgradeFree
            oncomplete={() => (showUpgradeModifyStatus = "standard")}
          />
        </div>
      {:else if showUpgradeModifyStatus === "modifystandard"}
        <ModifyStandard
          oncomplete={() => (showUpgradeModifyStatus = "standard")}
          oncancel={() => (showUpgradeModifyStatus = "standard")}
        />
      {:else}
        <h3 class="text-xl py-4 font-semibold">Your Plan</h3>
        <div class="border border-rounded border-gray-200 bg-white shadow">
          <div class="py-6 px-6">
            {#if signupStore.subscription.subscription_type === "free"}
              <div class="flex justify-between items-start gap-x-6">
                <div>
                  <p class="text-xl">Free Subscription</p>
                  <p class="text-sm mt-4 max-w-xs lg:max-w-md">
                    Your free subscription was started on {formatDate(
                      signupStore.subscription.created_at
                    )} and does not expire.
                  </p>
                </div>
                <div>
                  <div
                    class="px-2 py-2 bg-blue-50 rounded text-gray-800 text-sm text-center"
                  >
                    You have used <strong>{appStore.users.length}</strong> of
                    <strong>3</strong> available seats
                  </div>
                </div>
              </div>

              <div class="mt-6">
                <div
                  class="text-amber-700 bg-amber-50 py-1 px-2 rounded-sm text-sm"
                >
                  You are currently on our free plan. Upgrade to our <strong
                    >Standard Subscription</strong
                  > to unlock more features.
                </div>
              </div>
            {:else if signupStore.subscription.subscription_type === "unlimited"}
              <p class="text-xl">Legacy Business Success Plan Subscription</p>

              <div class="grid gap-4 mt-4">
                <div class="flex">
                  <div
                    class="px-2 py-2 bg-blue-50 rounded text-gray-800 text-sm text-center"
                  >
                    You have used <strong>{appStore.users.length}</strong> of
                    <strong>Unlimited</strong> seats.
                  </div>
                </div>
              </div>
            {:else}
              <p class="text-xl">{signupStore.subscription.name}</p>
            {/if}

            {#if stripe_subscription}
              <div class="mt-6">
                {#if stripe_subscription.status === "trialing"}
                  <p>
                    You are currently in your trial period, which will end on
                    <span class="font-bold"
                      >{dateConverter(stripe_data.subscription.trial_end)}</span
                    >.
                  </p>
                {:else if stripe_subscription.status === "active"}
                  <div>
                    Your subscription started on <span
                      class="font-bold bg-amber-50 px-1 py-0.5 rounded-sm text-sm border border-amber-100 shadow-sm"
                      >{dateConverter(stripe_subscription.start_date)}</span
                    >, and is currently active.
                  </div>
                  {#if stripe_upcoming_invoice}
                    <div class="mt-4 flex">
                      <div
                        class="px-2 py-1 bg-orange-50 rounded text-orange-800 text-sm border border-amber-100 shadow-sm"
                      >
                        Your subscription will renew on <span class="font-bold"
                          >{dateConverter(
                            stripe_upcoming_invoice.created
                          )}</span
                        >
                        for
                        <span class="font-bold"
                          >{formatter.format(
                            stripe_upcoming_invoice.amount_due / 100
                          )}</span
                        >.
                      </div>
                    </div>
                  {/if}
                {:else if stripe_subscription.status === "past_due"}
                  <p>
                    Your subscription is currently past due, please enter a
                    payment method so we can reactivate your subscription.
                  </p>
                {:else if stripe_subscription.status === "canceled"}
                  <p>
                    Your subscription was cancelled. If you would like to
                    re-active your subscription, please click the button below.
                  </p>
                {:else if stripe_subscription.status === "unpaid"}
                  <p>
                    Your subscription currently has an unpaid balance, please
                    enter a payment method to ensure your account remains
                    active.
                  </p>
                {/if}
              </div>
            {/if}
          </div>
          <div class="border-t border-gray-200">
            <div
              class="grid grid-cols-1 md:grid-cols-2 py-4 px-4 items-center justify-between"
            >
              <div>
                <p class="text-sm text-gray-800">
                  Have questions about your plan?
                  <a
                    class="ml-2 text-sm text-blue-600 hover:underline"
                    href="mailto:support@leadwithpurpose.com"
                    >Contact us Today</a
                  >
                </p>
              </div>
              <div class="flex justify-end items-center">
                {#if signupStore.subscription.subscription_type === "free"}
                  <button
                    onclick={() => (showUpgradeModifyStatus = "upgradefree")}
                    class="bg-accent hover:bg-lwp_blue hover:shadow py-3 px-6 text-core font-semibold text-md rounded-sm border border-gray-300"
                  >
                    Upgrade to Standard Subscription
                  </button>
                {:else if signupStore.subscription.subscription_type === "unlimited"}
                  <div
                    class="text-amber-700 text-sm bg-amber-50 p-2 border rounded border-amber-200 shadow"
                  >
                    You are subscribed to an unsupported legacy plan. Please
                    contact <a
                      class="text-blue-600 hover:underline"
                      href="mailto:support@leadwithpurpose.com"
                      >support@leadwithpurpose.com</a
                    > to switch to a supported plan.
                  </div>
                {:else}
                  <button
                    onclick={() => (showUpgradeModifyStatus = "modifystandard")}
                    class="bg-blue-100 hover:bg-blue-200 hover:shadow py-3 px-6 text-gray-700 rounded-sm text-sm border border-gray-300"
                    >Modify Subscription</button
                  >
                {/if}
              </div>
            </div>
          </div>
        </div>

        {#if signupStore.subscription.subscription_type !== "free"}
          <h3 class="text-xl py-4 font-semibold mt-6">Payment Information</h3>
          <div class="border border-rounded border-gray-200 bg-white shadow">
            <UpdateCreditCard oncomplete={() => initialize()} />
          </div>
        {/if}

        <h3 class="text-xl py-4 font-semibold mt-6">Invoice Information</h3>
        <div
          class="border border-rounded border-gray-200 bg-white shadow py-4 px-8"
        >
          <div class="grid grid-cols-10 w-full py-2 border-b border-gray-200">
            <div class="col-span-2">Status</div>
            <div class="col-span-2">Date</div>
            <div class="col-span-2">Amount</div>
            <div class="col-span-2">Payment</div>
            <div class="col-span-2"></div>
          </div>

          {#if stripe_upcoming_invoice}
            <div class="border-b border-gray-200">
              <div class="mt-2 grid grid-cols-10 w-full py-2 gap-x-2">
                <div class="col-span-2 flex items-center">
                  <span
                    class="text-yellow-500 font-semibold text-sm py-0.5 px-2 bg-yellow-50 border border-yellow-100 rounded-sm shadow-sm"
                    >Upcoming</span
                  >
                </div>
                <div class="col-span-2 text-sm flex items-center">
                  {dateConverter(stripe_upcoming_invoice.created)}
                </div>
                <div class="col-span-2 text-sm flex items-center">
                  {formatter.format(stripe_upcoming_invoice.amount_due / 100)}
                </div>
                <div class="col-span-2 text-sm flex items-center">N/A</div>
                <div class="col-span-2 flex"></div>
              </div>
            </div>
          {/if}
          {#each stripe_invoices as invoice}
            <div class="border-b border-gray-200">
              <div class="mt-2 grid grid-cols-10 w-full py-2 gap-x-2">
                <div class="col-span-2 text-sm flex items-center">
                  {#if invoice.status === "paid"}
                    <span
                      class="text-green-500 font-semibold py-0.5 px-2 bg-green-50 border border-green-100 rounded-sm shadow-sm"
                      >Paid</span
                    >
                  {:else if invoice.status === "open"}
                    <span
                      class="text-yellow-500 font-semibold py-0.5 px-2 bg-yellow-50 border border-yellow-100 rounded-sm shadow-sm"
                      >Open</span
                    >
                  {:else if invoice.status === "draft"}
                    <span
                      class="text-blue-500 font-semibold py-0.5 px-2 bg-blue-50 border border-blue-100 rounded-sm shadow-sm"
                      >Draft</span
                    >
                  {:else if invoice.status === "void"}
                    <span
                      class="text-red-500 font-semibold py-0.5 px-2 bg-red-50 border border-red-100 rounded-sm shadow-sm"
                      >Void</span
                    >
                  {:else if invoice.status === "uncollectible"}
                    <span
                      class="text-red-500 font-semibold py-0.5 px-2 bg-red-50 border border-red-100 rounded-sm shadow-sm"
                      >Uncollectible</span
                    >
                  {/if}
                </div>
                <div class="col-span-2 text-sm flex items-center">
                  {dateConverter(invoice.created)}
                </div>
                <div class="col-span-2 text-sm flex items-center">
                  {formatter.format(invoice.amount_due / 100)}
                </div>
                <div class="col-span-2 text-sm flex items-center">
                  {formatter.format(invoice.amount_paid / 100)}
                </div>
                <div class="col-span-2 flex items-center gap-x-2">
                  <a
                    href={`${invoice.hosted_invoice_url}`}
                    target="_blank"
                    class="text-blue-500 text-sm hover:underline hover:bg-blue-50 border border-blue-100 px-1 py-0.5 shadow-sm"
                    >Details</a
                  >
                  <a
                    href={`${invoice.invoice_pdf}`}
                    target="_blank"
                    class="text-blue-500 text-sm hover:underline hover:bg-blue-50 border border-blue-100 px-1 py-0.5 shadow-sm"
                    >Download</a
                  >
                </div>
              </div>
            </div>
          {:else}
            <div class="flex justify-center py-2">
              <p class="text-sm text-gray-700 p-1 bg-gray-50 rounded">
                You currently have no invoices available.
              </p>
            </div>
          {/each}
        </div>
      {/if}
    </div>
  </div>
</div>
